import dayjs from 'dayjs'
import { useMemo } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  LinearProgress,
  Typography,
} from '@mui/material'
import { Cancel, ArrowBack } from '@mui/icons-material'
import { UserIcon, CourseIcon, ConfirmIcon } from 'src/v3/core/icons'
import { LightweightUser } from 'src/v3/manage/manage.slice'
import { useLinkedServiceName } from '../../hooks/use-linked-service-name'

type AssignmentFormData = {
  name: string
  linkedServiceID: string
  dueDate: string
  userIDs: string[]
  courseIDs: string[]
}

type Course = {
  id: string
  name: string
  section?: string
  length?: number
}

type ChangesSummary = {
  addedUsers: string[]
  removedUsers: string[]
  addedCourses: string[]
  removedCourses: string[]
  hasChanges: boolean
}

type AssignmentReviewStepProps = {
  formData: AssignmentFormData
  onCancel: () => void
  onBack: () => void
  onSubmit: () => void
  onShowUsersSummary: () => void
  onShowCoursesSummary: () => void
  isSubmitting: boolean
  isEditMode?: boolean
  changesSummary?: ChangesSummary
  users?: LightweightUser[]
  courses?: Course[]
}

export default function AssignmentReviewStep({
  formData,
  onCancel,
  onBack,
  onSubmit,
  onShowUsersSummary,
  onShowCoursesSummary,
  isSubmitting,
  isEditMode = false,
  changesSummary,
  users = [],
  courses = [],
}: AssignmentReviewStepProps) {
  let linkedServiceName = useLinkedServiceName(formData.linkedServiceID)

  let getUserName = (userID: string) => {
    let user = users.find((u) => u.id === userID)
    return user ? `${user.firstName} ${user.lastName}` : userID
  }

  let getCourseName = (courseID: string) => {
    let course = courses.find((c) => c.id === courseID)
    return course ? course.name : courseID
  }

  let categoryBreakdown = useMemo(() => {
    let selectedCourses = formData.courseIDs
      .map((id) => courses.find((c) => c.id === id))
      .filter(Boolean) as Course[]

    let map = new Map<string, { count: number; hours: number }>()
    selectedCourses.forEach((course) => {
      let key = course.section || 'Uncategorized'
      let existing = map.get(key) || { count: 0, hours: 0 }
      map.set(key, {
        count: existing.count + 1,
        hours: existing.hours + (course.length || 0),
      })
    })

    return Array.from(map.entries())
      .map(([category, stats]) => ({ category, ...stats }))
      .toSorted((a, b) => a.category.localeCompare(b.category))
  }, [formData.courseIDs, courses])

  return (
    <>
      <Card className="p-6 mb-6">
        <Typography variant="h6" component="h2" className="mb-4">
          {isEditMode ? 'Review Changes' : 'Review Assignment'}
        </Typography>

        <Box className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Card sx={{ bgcolor: 'background.paperDark' }} className="p-6">
            <Typography variant="h6" className="mb-4">
              Assignment Details
            </Typography>
            <Box className="flex flex-col gap-4">
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  className="mb-1"
                >
                  Name:
                </Typography>
                <Typography variant="body1">{formData.name}</Typography>
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  className="mb-1"
                >
                  Linked Service:
                </Typography>
                <Typography variant="body1">{linkedServiceName}</Typography>
              </Box>
              {formData.dueDate && (
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    className="mb-1"
                  >
                    Due Date:
                  </Typography>
                  <Typography variant="body1">
                    {dayjs(formData.dueDate).format('MMMM D, YYYY')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Card>

          <Card sx={{ bgcolor: 'background.paperDark' }} className="p-6">
            <Typography variant="h6" className="mb-4">
              Summary
            </Typography>
            <Box className="flex flex-col gap-4">
              <Box className="flex items-center gap-2">
                <UserIcon sx={{ color: 'primary.main' }} />
                <Chip
                  label={`${formData.userIDs.length} users selected`}
                  color="primary"
                  variant="outlined"
                  onClick={onShowUsersSummary}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'primary.light' },
                  }}
                />
              </Box>
              <Box className="flex items-center gap-2">
                <CourseIcon sx={{ color: 'primary.main' }} />
                <Chip
                  label={`${formData.courseIDs.length} courses selected`}
                  color="primary"
                  variant="outlined"
                  onClick={onShowCoursesSummary}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'primary.light' },
                  }}
                />
              </Box>
              {categoryBreakdown.length > 0 && (
                <>
                  <Divider className="my-2" />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    className="mb-2"
                  >
                    Hours by Category:
                  </Typography>
                  <Box className="flex flex-col gap-1">
                    {categoryBreakdown.map(({ category, count, hours }) => (
                      <Box
                        key={category}
                        className="flex items-center justify-between"
                      >
                        <Typography variant="body2">{category}</Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          className="ml-4 whitespace-nowrap"
                        >
                          {count} course{count !== 1 ? 's' : ''} &middot;{' '}
                          {hours.toFixed(1)} hrs
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          </Card>
        </Box>
      </Card>
      {isEditMode && changesSummary && changesSummary.hasChanges && (
        <Card className="p-6 mb-6">
          <Typography variant="h6" component="h2" className="mb-4">
            Changes Summary
          </Typography>
          <Box className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {(changesSummary.addedUsers.length > 0 ||
              changesSummary.removedUsers.length > 0) && (
              <Card sx={{ bgcolor: 'background.paperDark' }} className="p-6">
                <Box className="flex items-center gap-2 mb-4">
                  <UserIcon sx={{ color: 'primary.main' }} />
                  <Typography variant="subtitle1" className="font-semibold">
                    User Changes
                  </Typography>
                </Box>

                {changesSummary.addedUsers.length > 0 && (
                  <Box className="mb-4">
                    <Typography
                      color="success.main"
                      variant="body2"
                      className="mb-2 font-semibold"
                    >
                      Added ({changesSummary.addedUsers.length}):
                    </Typography>
                    <Box
                      sx={{
                        borderColor: 'success.main',
                        bgcolor: 'success.dark',
                      }}
                      className="p-4 border rounded max-h-[150px] overflow-y-auto"
                    >
                      {changesSummary.addedUsers.map((userID) => (
                        <Typography
                          key={userID}
                          variant="body2"
                          className="mb-1"
                        >
                          • {getUserName(userID)}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}

                {changesSummary.removedUsers.length > 0 && (
                  <Box>
                    <Typography
                      color="error.main"
                      variant="body2"
                      className="mb-2 font-semibold"
                    >
                      Removed ({changesSummary.removedUsers.length}):
                    </Typography>
                    <Box
                      sx={{
                        borderColor: 'error.main',
                        bgcolor: 'error.dark',
                      }}
                      className="p-4 border rounded max-h-[150px] overflow-y-auto"
                    >
                      {changesSummary.removedUsers.map((userID) => (
                        <Typography
                          key={userID}
                          variant="body2"
                          className="mb-1"
                        >
                          • {getUserName(userID)}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}
              </Card>
            )}

            {(changesSummary.addedCourses.length > 0 ||
              changesSummary.removedCourses.length > 0) && (
              <Card sx={{ bgcolor: 'background.paperDark' }} className="p-6">
                <Box className="flex items-center gap-2 mb-4">
                  <CourseIcon sx={{ color: 'primary.main' }} />
                  <Typography variant="subtitle1" className="font-semibold">
                    Course Changes
                  </Typography>
                </Box>

                {changesSummary.addedCourses.length > 0 && (
                  <Box className="mb-4">
                    <Typography
                      color="success.main"
                      variant="body2"
                      className="mb-2 font-semibold"
                    >
                      Added ({changesSummary.addedCourses.length}):
                    </Typography>
                    <Box
                      sx={{
                        borderColor: 'success.main',
                        bgcolor: 'success.dark',
                      }}
                      className="p-4 border rounded max-h-[150px] overflow-y-auto"
                    >
                      {changesSummary.addedCourses.map((courseID) => (
                        <Typography
                          key={courseID}
                          variant="body2"
                          className="mb-1"
                        >
                          • {getCourseName(courseID)}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}

                {changesSummary.removedCourses.length > 0 && (
                  <Box>
                    <Typography
                      color="error.main"
                      variant="body2"
                      className="mb-2 font-semibold"
                    >
                      Removed ({changesSummary.removedCourses.length}):
                    </Typography>
                    <Box
                      sx={{
                        borderColor: 'error.main',
                        bgcolor: 'error.dark',
                      }}
                      className="p-4 border rounded max-h-[150px] overflow-y-auto"
                    >
                      {changesSummary.removedCourses.map((courseID) => (
                        <Typography
                          key={courseID}
                          variant="body2"
                          className="mb-1"
                        >
                          • {getCourseName(courseID)}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}
              </Card>
            )}
          </Box>
        </Card>
      )}
      {isSubmitting && (
        <Box className="mb-6">
          <LinearProgress />
          <Typography
            variant="body2"
            color="text.secondary"
            className="mt-2 text-center"
          >
            {isEditMode ? 'Updating assignment...' : 'Creating assignment...'}
          </Typography>
        </Box>
      )}
      <Box className="flex justify-end gap-3">
        <Button
          variant="outlined"
          onClick={onCancel}
          size="large"
          startIcon={<Cancel />}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant="outlined"
          onClick={onBack}
          size="large"
          startIcon={<ArrowBack />}
          disabled={isSubmitting}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={isSubmitting}
          size="large"
          startIcon={<ConfirmIcon />}
        >
          {isSubmitting
            ? isEditMode
              ? 'Saving...'
              : 'Creating...'
            : isEditMode
              ? 'Save Changes'
              : 'Create Assignment'}
        </Button>
      </Box>
    </>
  )
}
